import React, { useState } from "react";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { useTranslation } from "react-i18next";

interface Transaction {
  id: number;
  amount: string;
}

interface TransactionRefundPopupProps {
  transaction: Transaction;
  onClose: () => void;
  onRefundComplete: () => void;
}

const TransactionRefundPopup: React.FC<TransactionRefundPopupProps> = ({
  transaction,
  onClose,
  onRefundComplete,
}) => {
  const [refundAmount, setRefundAmount] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [axiosInstance] = useAxiosInstance();
  const { t } = useTranslation();
  const formattedAmount = Number(refundAmount).toFixed(2);

  const handleRefundAmountChange = (value: string) => {
    const regex = /^\d*\.?\d{0,2}$/;
    if (regex.test(value)) {
      setRefundAmount(value);
    }
  };

  const handleRefund = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post(
        `api/v1/transaction/${transaction.id}/refund/`,
        {
          amount: formattedAmount,
        },
      );

      if (response.status === 200) {
        onRefundComplete();
      }
    } catch (error) {
      console.error("Error processing refund:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="h-auto w-full max-w-lg rounded-lg bg-white p-4 shadow-lg">
        <h2 className="overflow-wrap break-word max-w-full whitespace-normal text-center text-lg font-bold">
          {t("back.management.transaction.enterRefundAmount")}
        </h2>
        <p className="mt-2 text-center text-gray-600">
          {t("back.management.transaction.maxRefundAmount")}: $
          {transaction.amount}
        </p>
        <input
          type="text"
          className="mt-4 w-full rounded border px-4 py-2"
          placeholder={t("back.management.transaction.refundAmount")}
          value={refundAmount}
          onChange={(e) => handleRefundAmountChange(e.target.value)}
        />
        <div className="mt-4 flex justify-end">
          <button
            className="mr-2 rounded bg-red-500 px-4 py-2 text-white disabled:opacity-50"
            onClick={handleRefund}
            disabled={
              !refundAmount ||
              parseFloat(refundAmount) > parseFloat(transaction.amount) ||
              isLoading
            }
          >
            {isLoading
              ? t("back.management.transaction.processing")
              : t("back.management.transaction.confirm")}
          </button>
          <button
            className="rounded bg-gray-400 px-4 py-2 text-white"
            onClick={onClose}
          >
            {t("back.management.transaction.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionRefundPopup;
